import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/ProductCard';
import { FiFilter, FiX } from 'react-icons/fi';
import './ShopPage.css';

const CATEGORIES = [
  { label: 'All', value: '' },
  { label: 'Bracelets', value: 'bracelets' },
  { label: 'Necklaces', value: 'necklaces' },
  { label: 'Rings', value: 'rings' },
  { label: 'Hand Chains', value: 'hand-chains' },
];

const ShopPage = () => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);

  const [filters, setFilters] = useState({
    category: params.get('category') || '',
    search: params.get('search') || '',
    sort: params.get('sort') || 'newest',
    sale: params.get('sale') || '',
    page: 1,
  });

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const q = new URLSearchParams();
      if (filters.category) q.set('category', filters.category);
      if (filters.search) q.set('search', filters.search);
      if (filters.sort) q.set('sort', filters.sort);
      if (filters.sale) q.set('sale', filters.sale);
      q.set('page', filters.page);
      q.set('limit', '20');
      const res = await api.get(`/products?${q}`);
      setProducts(res.data.products || []);
      setTotal(res.data.pagination?.total || 0);
    } catch (err) {
      console.error('Products fetch error:', err);
      setError('Failed to load products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setFilters(f => ({
      ...f,
      category: p.get('category') || '',
      search: p.get('search') || '',
      sale: p.get('sale') || '',
    }));
  }, [location.search]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [filters.category]);

  const setFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value, page: 1 }));
  };

  return (
    <div className="shop-page">
      <div className="page-header">
        <h1>{filters.category ? filters.category.charAt(0).toUpperCase() + filters.category.slice(1).replace('-', ' ') : 'All Jewellery'}</h1>
        <p>{total} products</p>
      </div>

      <div className="container">
        <div className="shop-layout">
          {/* Sidebar */}
          <aside className={`shop-sidebar ${filterOpen ? 'open' : ''}`}>
            <div className="sidebar-header">
              <h3>Filter</h3>
              <button className="close-filter" onClick={() => setFilterOpen(false)}><FiX /></button>
            </div>
            <div className="filter-group">
              <h4>Category</h4>
              {CATEGORIES.map(cat => (
                <label key={cat.value} className={`filter-option ${filters.category === cat.value ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="category"
                    value={cat.value}
                    checked={filters.category === cat.value}
                    onChange={() => setFilter('category', cat.value)}
                  />
                  {cat.label}
                </label>
              ))}
            </div>
            <div className="filter-group">
              <h4>Offers</h4>
              <label className={`filter-option ${filters.sale === 'true' ? 'active' : ''}`}>
                <input
                  type="checkbox"
                  checked={filters.sale === 'true'}
                  onChange={(e) => setFilter('sale', e.target.checked ? 'true' : '')}
                />
                On Sale
              </label>
            </div>
          </aside>

          {/* Main */}
          <div className="shop-main">
            {/* Toolbar */}
            <div className="shop-toolbar">
              <button className="btn btn-outline btn-sm filter-toggle" onClick={() => setFilterOpen(true)}>
                <FiFilter size={14} /> Filters
              </button>
              {filters.search && <span className="search-tag">"{filters.search}" <button onClick={() => setFilter('search', '')}>×</button></span>}
              <select
                className="sort-select"
                value={filters.sort}
                onChange={(e) => setFilter('sort', e.target.value)}
              >
                <option value="newest">Newest First</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="popular">Most Popular</option>
              </select>
            </div>

            {loading ? (
              <div className="loading-spinner"><div className="spinner" /></div>
            ) : error ? (
              <div className="empty-state">
                <h3>Oops!</h3>
                <p>{error}</p>
                <button className="btn btn-primary" onClick={fetchProducts}>Try Again</button>
              </div>
            ) : products.length > 0 ? (
              <div className="products-grid">
                {products.map(p => <ProductCard key={p._id} product={p} />)}
              </div>
            ) : (
              <div className="empty-state">
                <h3>No Products Found</h3>
                <p>Try adjusting your filters or search term.</p>
                <button className="btn btn-primary" onClick={() => setFilters({ category: '', search: '', sort: 'newest', sale: '', page: 1 })}>
                  Clear Filters
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {filterOpen && <div className="overlay" onClick={() => setFilterOpen(false)} />}
    </div>
  );
};

export default ShopPage;
